/**
 * Org Dashboard — live PostgreSQL bpanalytics mirror of the Grafana 'Home' dashboard.
 * Replaces the legacy StatsBoard at /dashboard.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { CommodityUsageChart } from './CommodityUsageChart';
import { ChartCardMenu } from './ChartCardMenu';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import {
  CalendarIcon, Download, Lightbulb, Flame, Waves, Briefcase,
  Building, RefreshCw, ChevronDown, ChevronRight, AlertCircle,
  Home, Info,
} from 'lucide-react';
import { format, subMonths, startOfMonth } from 'date-fns';
import { toast } from 'sonner';
import L from 'leaflet';
import './org-dashboard.css';

// ─── helpers ───────────────────────────────────────────────────────────────
const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtMoneyFull = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtUsage = (n, unit) => {
  const v = Number(n) || 0;
  // Unit cascade: gal → kgal → Mgal → Ggal → Tgal
  if (unit === 'gal') {
    if (v >= 1e12) return { val: (v / 1e12).toFixed(2), unit: 'T gal' };
    if (v >= 1e9) return { val: (v / 1e9).toFixed(2), unit: 'B gal' };
    if (v >= 1e6) return { val: (v / 1e6).toFixed(2), unit: 'M gal' };
    if (v >= 1e3) return { val: (v / 1e3).toFixed(2), unit: 'k gal' };
    return { val: v.toFixed(0), unit: 'gal' };
  }
  if (unit === 'kWh') {
    if (v >= 1e9) return { val: (v / 1e9).toFixed(2), unit: 'TWh' };
    if (v >= 1e6) return { val: (v / 1e6).toFixed(2), unit: 'GWh' };
    if (v >= 1e3) return { val: (v / 1e3).toFixed(2), unit: 'MWh' };
    return { val: v.toFixed(0), unit: 'kWh' };
  }
  if (unit === 'therms') {
    if (v >= 1e6) return { val: (v / 1e6).toFixed(2), unit: 'M therms' };
    if (v >= 1e3) return { val: (v / 1e3).toFixed(2), unit: 'k therms' };
    return { val: v.toFixed(0), unit: 'therms' };
  }
  if (unit === 'tons') {
    if (v >= 1e6) return { val: (v / 1e6).toFixed(2), unit: 'M tons' };
    if (v >= 1e3) return { val: (v / 1e3).toFixed(2), unit: 'k tons' };
    return { val: v.toFixed(2), unit: 'tons' };
  }
  return { val: v.toFixed(2), unit };
};

const COLORS = {
  electricity: '#fb7185',  // rose-400 — modern flat
  gas: '#fb923c',          // orange-400
  water: '#38bdf8',        // sky-400
  sewer: '#a78bfa',        // violet-400
  trash: '#94a3b8',        // slate-400
  other: '#cbd5e1',
};

const COMMODITY_GRADIENTS = {
  electricity: 'from-rose-500 via-red-500 to-pink-500',
  gas: 'from-amber-400 via-orange-500 to-orange-600',
  water: 'from-sky-400 via-blue-500 to-blue-600',
  trash: 'from-slate-500 via-slate-600 to-slate-700',
};

// ─── multi-select pill ─────────────────────────────────────────────────────
function MultiSelect({ label, value, options, onChange, dataTestid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const sel = value || [];
  const filtered = (options || []).filter((o) =>
    String(o.label).toLowerCase().includes(search.toLowerCase()),
  );
  const toggle = (val) => {
    if (sel.includes(val)) onChange(sel.filter((v) => v !== val));
    else onChange([...sel, val]);
  };
  const display = sel.length === 0 ? 'All' : sel.length === 1 ? sel[0] : `${sel.length} selected`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 min-w-[140px] justify-between gap-2 border-slate-200 bg-white px-3 text-xs font-normal hover:bg-slate-50"
          data-testid={dataTestid}
        >
          <span className="font-medium text-slate-500">{label}</span>
          <span className="truncate font-medium text-slate-900">{display}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="border-b p-2">
          <Input
            placeholder={`Search ${label.toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {sel.length > 0 && (
            <button
              type="button"
              className="mb-1 w-full rounded px-2 py-1 text-left text-xs text-blue-600 hover:bg-blue-50"
              onClick={() => onChange([])}
            >
              Clear all
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="p-3 text-center text-xs text-muted-foreground">No matches</div>
          ) : filtered.map((o) => (
            <label
              key={o.value}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={sel.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="h-3.5 w-3.5"
              />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── date range ────────────────────────────────────────────────────────────
function DateRangePicker({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 border-slate-200 bg-white px-3 text-xs font-normal hover:bg-slate-50"
          data-testid="org-dash-date-range"
        >
          <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-medium text-slate-500">Date</span>
          <span className="font-medium text-slate-900">
            {from ? format(from, 'MMM d, yyyy') : 'Start'} – {to ? format(to, 'MMM d, yyyy') : 'End'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="end">
        <div className="mb-2 flex flex-wrap gap-1">
          {[
            { label: '6M', m: 6 },
            { label: '12M', m: 12 },
            { label: '24M', m: 24 },
            { label: 'YTD', ytd: true },
          ].map((p) => (
            <Button
              key={p.label}
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => {
                const t = new Date();
                const f = p.ytd ? new Date(t.getFullYear(), 0, 1) : startOfMonth(subMonths(t, p.m));
                onChange({ from: f, to: t });
                setOpen(false);
              }}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <Calendar
          mode="range"
          selected={{ from, to }}
          onSelect={(r) => onChange({ from: r?.from, to: r?.to })}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}

// ─── main ──────────────────────────────────────────────────────────────────
export default function OrgDashboardPage() {
  const currentAccountStr = typeof window !== 'undefined' ? localStorage.getItem('current_account') : null;
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  const accountId = currentAccount?.id;

  const [range, setRange] = useState(() => ({
    from: startOfMonth(subMonths(new Date(), 12)),
    to: new Date(),
  }));
  const [stateFilter, setStateFilter] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState([]);
  const [siteFilter, setSiteFilter] = useState([]);

  const [filterOpts, setFilterOpts] = useState({ states: [], suppliers: [], sites: [] });
  const [usage, setUsage] = useState(null);
  const [mongoKpis, setMongoKpis] = useState(null);
  const [monthly, setMonthly] = useState({ series: [], totals: {} });
  const [comparison, setComparison] = useState({ items: [], grand_total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const [expanded, setExpanded] = useState({});
  const monthlySpendRef = useRef(null);

  const queryParams = useMemo(() => {
    const p = {
      account_id: accountId,
      date_from: range.from?.toISOString().slice(0, 10),
      date_to: range.to?.toISOString().slice(0, 10),
    };
    if (stateFilter.length) p.state = stateFilter.join(',');
    if (supplierFilter.length) p.supplier = supplierFilter.join(',');
    if (siteFilter.length) p.site = siteFilter.join(',');
    return p;
  }, [accountId, range, stateFilter, supplierFilter, siteFilter]);

  const loadAll = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      // Use allSettled so one slow/dead endpoint (e.g. PG SSL drop) doesn't
      // blank the entire dashboard. Each panel renders independently.
      // All panels are now Mongo-backed (Map coords come from ubm_locations
      // via a one-off PG → Mongo backfill — see backfill_location_coordinates.py).
      const results = await Promise.allSettled([
        api.get('/dashboard/org/link-status', { params: { account_id: accountId } }),
        api.get('/dashboard/org/mongo/filter-options', { params: { account_id: accountId } }),
        // Standardised UOM usage rollups (kWh / therms / gallons) — drives commodity tiles
        api.get('/dashboard/org/mongo/commodity-usage', { params: queryParams }),
        // Mongo-backed Monthly Spend chart (bills.current_bill_amount)
        api.get('/dashboard/org/mongo/monthly-spend', { params: queryParams }),
        // Mongo-backed Spend Comparison (bills grouped by service address, with lat/lng)
        api.get('/dashboard/org/mongo/spend-comparison', { params: queryParams }),
        // PF Bill Spend + Paid Bill Spend tile data
        api.get('/dashboard/org/mongo/kpis', { params: { account_id: accountId, ...(queryParams.date_from ? { date_from: queryParams.date_from, date_to: queryParams.date_to } : {}) } }),
      ]);
      const [link, opts, usageRes, mon, cmp, kpis] = results;
      if (link.status === 'fulfilled') setLinkInfo(link.value.data);
      if (opts.status === 'fulfilled') setFilterOpts(opts.value.data);
      if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data);
      if (mon.status === 'fulfilled') setMonthly(mon.value.data);
      if (cmp.status === 'fulfilled') setComparison(cmp.value.data);
      if (kpis.status === 'fulfilled') setMongoKpis(kpis.value.data);

      // Surface a non-blocking warning if any endpoint failed (so user sees
      // partial data plus an explanatory toast, instead of silent zeros).
      const failures = results
        .map((r, i) => ({ r, name: ['link', 'filter-options', 'commodity-usage', 'monthly', 'spend-comparison', 'kpis'][i] }))
        .filter(({ r }) => r.status === 'rejected');
      if (failures.length > 0) {
        const detail0 = failures[0].r.reason?.response?.data?.detail;
        if (typeof detail0 === 'object' && detail0?.code === 'PG_CUSTOMER_NOT_LINKED') {
          setError({ kind: 'not-linked', detail: detail0 });
        } else {
          const names = failures.map(f => f.name).join(', ');
          toast.warning(`Some panels failed to load: ${names}. Showing partial data.`);
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'PG_CUSTOMER_NOT_LINKED') {
        setError({ kind: 'not-linked', detail });
      } else {
        setError({ kind: 'generic', message: detail?.message || detail || e.message });
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to load dashboard'));
      }
    } finally {
      setLoading(false);
    }
  }, [accountId, queryParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── early states ────────────────────────────────────────────────────────
  if (!accountId) {
    return (
      <MainLayout>
        <div className="p-8 text-center text-muted-foreground">No account selected.</div>
      </MainLayout>
    );
  }

  if (error?.kind === 'not-linked') {
    return (
      <MainLayout>
        <div className="mx-auto max-w-2xl p-8" data-testid="org-dash-not-linked">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
                <div>
                  <h3 className="text-base font-semibold text-amber-900">Org not linked to bpanalytics</h3>
                  <p className="mt-1 text-sm text-amber-800">
                    <strong>{error.detail.organisation_name}</strong> is not yet linked to a customer in
                    the bpanalytics database. Ask a Super Admin to link it from Settings → System → PG Mapping.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <TooltipProvider delayDuration={150}>
        <div className="space-y-4 p-4" data-testid="org-dashboard-page">
          {/* ─── Title + Refresh + Filters all in one row ─── */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-xl font-semibold text-slate-900">
              <Home className="h-5 w-5 text-slate-500" />
              Dashboard
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 gap-1.5 text-muted-foreground hover:text-slate-900"
              onClick={loadAll}
              disabled={loading}
              data-testid="org-dash-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <div className="ml-auto flex flex-wrap items-center gap-2" data-testid="filter-bar">
              <MultiSelect
                label="State"
                value={stateFilter}
                onChange={setStateFilter}
                options={(filterOpts.states || []).map((s) => ({ value: s, label: s }))}
                dataTestid="filter-state"
              />
              <MultiSelect
                label="Supplier"
                value={supplierFilter}
                onChange={setSupplierFilter}
                options={(filterOpts.suppliers || []).map((s) => ({ value: s, label: s }))}
                dataTestid="filter-supplier"
              />
              <MultiSelect
                label="Property"
                value={siteFilter}
                onChange={setSiteFilter}
                options={(filterOpts.sites || []).map((s) => ({ value: s.uid, label: s.sitename }))}
                dataTestid="filter-site"
              />
              <DateRangePicker from={range.from} to={range.to} onChange={setRange} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            {/* Spend Comparison — 3 cols, no card chrome */}
            <div className="lg:col-span-3" data-testid="spend-comparison-card">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold tracking-tight text-slate-900">Spend Comparison</div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => downloadCsv(comparison.items)}
                  data-testid="spend-comparison-download"
                >
                  <Download className="h-3 w-3" /> Download
                </Button>
              </div>
              <div className="h-[400px] overflow-y-auto rounded-md border border-slate-200/70">
                <table className="w-full text-sm" data-testid="spend-comparison-table">
                  <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">Property</th>
                      <th className="px-2 py-1.5 text-right text-[11px] font-medium uppercase tracking-wide text-slate-500">Total Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(comparison.items || []).map((row) => {
                      const isExp = !!expanded[row.site_uid];
                      return (
                        <Fragment key={row.site_uid}>
                          <tr
                            className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                            onClick={() => setExpanded((e) => ({ ...e, [row.site_uid]: !e[row.site_uid] }))}
                          >
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1">
                                {isExp ? <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" /> : <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />}
                                <span
                                  className={`truncate text-[13px] ${row.unmapped ? 'italic text-amber-700' : 'text-slate-800'}`}
                                  title={row.unmapped ? `Unlinked address — needs UBM mapping: ${row.sitename}` : row.sitename}
                                >
                                  {row.sitename || '—'}
                                </span>
                                {row.unmapped ? (
                                  <span className="ml-auto rounded-sm bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-700">
                                    UNLINKED
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 text-right text-[13px] font-medium tabular-nums text-slate-900">
                              {fmtMoney(row.total_spend)}
                            </td>
                          </tr>
                          {isExp && row.by_commodity && (
                            <tr className="bg-slate-50/60">
                              <td colSpan={2} className="px-2 py-1.5">
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(row.by_commodity)
                                    .filter(([, v]) => (v?.spend || 0) > 0)
                                    .map(([k, v]) => (
                                      <div
                                        key={k}
                                        className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px]"
                                        style={{ borderLeft: `3px solid ${COLORS[k] || '#94a3b8'}` }}
                                      >
                                        <span className="capitalize text-slate-500">{k}: </span>
                                        <span className="font-semibold text-slate-800">{fmtMoney(v.spend)}</span>
                                      </div>
                                    ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                    <tr className="border-t border-slate-200 bg-slate-50">
                      <td className="px-2 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-600">Total</td>
                      <td className="px-2 py-1.5 text-right text-[13px] font-bold tabular-nums text-slate-900">{fmtMoney(comparison.grand_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {comparison.unmapped_count > 0 ? (
                <div className="mt-1.5 text-[11px] text-amber-700" data-testid="spend-comparison-unmapped-hint">
                  {comparison.unmapped_count} address{comparison.unmapped_count === 1 ? '' : 'es'} not yet linked to a property — {fmtMoney(comparison.unmapped_spend)} of spend.
                </div>
              ) : null}
            </div>

            {/* Map — 3 cols, height aligned to Spend Comparison */}
            <div className="lg:col-span-3" data-testid="sites-map-card">
              <div className="mb-2 text-sm font-semibold opacity-0 select-none">.</div>
              <MapErrorBoundary>
                <SitesMap items={comparison.items || []} />
              </MapErrorBoundary>
            </div>

            {/* Chart + KPIs — 6 cols */}
            <div className="space-y-3 lg:col-span-6">
              <div data-testid="monthly-spend-card" ref={monthlySpendRef}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-semibold tracking-tight text-slate-900">Monthly Spend</div>
                  <ChartCardMenu
                    captureRef={monthlySpendRef}
                    csvFilename="monthly-spend"
                    title="Monthly Spend — bill-level detail"
                    description="One row per bill within the active filters. Includes Charge and Total Amount Due."
                    testidPrefix="monthly-spend-chart"
                    csvHeader={[
                      'Sitename', 'Account', 'Address', 'Vendor',
                      'Bill Period From', 'Bill Period To', 'Issue Date',
                      'Commodity', 'Charge', 'Total Amount Due',
                    ]}
                    onFetchCsv={async () => {
                      const r = await api.get('/dashboard/org/mongo/monthly-spend-export',
                        { params: queryParams });
                      return r.data?.rows || [];
                    }}
                    mapRowToCells={(r) => [
                      r.sitename, r.account, r.address, r.vendor,
                      r.period_from, r.period_to, r.issue_date,
                      r.commodity, r.charge, r.total_amount_due,
                    ]}
                  >
                    <MonthlySpendBarChart monthly={monthly} large />
                  </ChartCardMenu>
                </div>
                <div className="h-[300px]">
                  <MonthlySpendBarChart monthly={monthly} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <TotalSpendTile
                  totalSpend={mongoKpis?.total_spend}
                  requested={mongoKpis?.pf_bill_spend}
                  pulled={mongoKpis?.pulled_amount}
                  paid={mongoKpis?.paid_bill_spend}
                  bankDebits={mongoKpis?.bank_debits_total}
                  testid="kpi-total-spend"
                />
                <PropertiesTile
                  properties={mongoKpis?.properties_count ?? 0}
                  units={mongoKpis?.units_count ?? 0}
                  meters={mongoKpis?.meters_count ?? 0}
                  accounts={mongoKpis?.accounts_count ?? 0}
                  testid="kpi-sites"
                />
              </div>
            </div>
          </div>

          {/* ─── 3 commodity gradient cards (with monthly bar chart) ─── */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div data-testid="commodity-card-electricity">
              <CommodityHero
                gradient={COMMODITY_GRADIENTS.electricity}
                icon={<Lightbulb className="h-7 w-7" />}
                title="Electricity"
                {...fmtUsage(usage?.electricity_kwh, 'kWh')}
                testid="commodity-electricity"
              />
              <CommodityUsageChart
                commodity="electricity"
                series={usage?.series}
                accountId={accountId}
                queryParams={queryParams}
                title="Electricity"
              />
              <CommodityFooterStats meta={usage?.commodity_meta?.electricity} commodity="electricity" />
            </div>
            <div data-testid="commodity-card-gas">
              <CommodityHero
                gradient={COMMODITY_GRADIENTS.gas}
                icon={<Flame className="h-7 w-7" />}
                title="Gas"
                {...fmtUsage(usage?.gas_therms, 'therms')}
                testid="commodity-gas"
              />
              <CommodityUsageChart
                commodity="gas"
                series={usage?.series}
                accountId={accountId}
                queryParams={queryParams}
                title="Gas"
              />
              <CommodityFooterStats meta={usage?.commodity_meta?.gas} commodity="gas" />
            </div>
            <div data-testid="commodity-card-water">
              <CommodityHero
                gradient={COMMODITY_GRADIENTS.water}
                icon={<Waves className="h-7 w-7" />}
                title="Water"
                {...fmtUsage(usage?.water_gallons, 'gal')}
                testid="commodity-water"
              />
              <CommodityUsageChart
                commodity="water"
                series={usage?.series}
                accountId={accountId}
                queryParams={queryParams}
                title="Water"
              />
              <CommodityFooterStats meta={usage?.commodity_meta?.water} commodity="water" />
            </div>
          </div>
        </div>
      </TooltipProvider>
    </MainLayout>
  );
}

// ─── sub-components ──────────────────────────────────────────────────────────
function KpiTile({ title, value, subValue, icon, accent = 'text-slate-900', testid }) {
  return (
    <Card className="border-slate-200/70 shadow-none" data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{title}</span>
          {icon}
        </div>
        <div className={`mt-1.5 text-xl font-bold ${accent}`}>{value}</div>
        {subValue ? (
          <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={testid ? `${testid}-sub` : undefined}>
            {subValue}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}


/**
 * Total Spend tile: big top number = total amount due across all bills
 * (`bills.total_amount_due`, includes any past-due/arrears so it matches
 * Spend Comparison grand_total). Three sub-stats below mirror the Org >
 * Payments > Payment File invoice_summary widget so they reconcile:
 *   • Requested   = sum(pf_bills.payment_amount)
 *   • Pulled      = bank statement credits + legacy pm_funds_pulled
 *   • Paid        = sum(pf_bills.paid_amount)
 * Invariant: Total Spend ≥ Requested ≥ Pulled ≥ Paid.
 */
function TotalSpendTile({ totalSpend, requested, pulled, paid, bankDebits, testid }) {
  return (
    <Card className="border-slate-200/70 shadow-none" data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Total Spend</span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                  data-testid={`${testid}-info`}
                  aria-label="How are these figures derived?"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-80 p-0 text-xs"
                data-testid={`${testid}-info-popover`}
              >
                <div className="border-b bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                  How each figure is derived
                </div>
                <dl className="divide-y divide-slate-100 text-[11.5px] leading-snug">
                  <DerivationRow
                    label="Total Spend"
                    accent="text-emerald-600"
                    formula="sum(bills.total_amount_due)"
                    description="Total amount due across every bill in the date range — current charges plus any past-due / arrears carried forward. Same source as the Spend Comparison grand-total."
                  />
                  <DerivationRow
                    label="Requested"
                    formula="sum(pm_payment_file_bills.payment_amount)"
                    description="What was scheduled for payment via Payment Files. Can include arrears from earlier bills, so it may be lower or higher than Total Spend depending on which bills are bundled into PFs."
                  />
                  <DerivationRow
                    label="Pulled"
                    formula="sum(pm_bank_statement_lines.credit, is_fund_pull=True) − R03 reversals + legacy pm_funds_pulled"
                    description="Net actual fund pulls — bank-statement credits tagged as fund pulls, less any R03 (ACH return) reversals, plus any legacy ledger entries."
                  />
                  <DerivationRow
                    label="Paid"
                    formula="sum(pm_payment_file_bills.paid_amount)"
                    description="What's been recorded against requested invoices. Bank statement debits may differ while transactions are being verified and updated — see the tooltip on the Paid tile."
                  />
                </dl>
              </PopoverContent>
            </Popover>
          </div>
          <Briefcase className="h-4 w-4 text-emerald-500" />
        </div>
        <div
          className="mt-1 text-xl font-bold tabular-nums text-emerald-600"
          data-testid={`${testid}-amount`}
        >
          {fmtMoney(totalSpend)}
        </div>
        <div className="mt-1.5 grid grid-cols-3 gap-2 border-t border-slate-100 pt-1.5">
          <div data-testid={`${testid}-requested`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Requested</div>
            <div className="text-xs font-semibold tabular-nums leading-tight text-slate-700">
              {fmtMoney(requested)}
            </div>
          </div>
          <div className="border-l border-slate-200 pl-2" data-testid={`${testid}-pulled`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pulled</div>
            <div className="text-xs font-semibold tabular-nums leading-tight text-slate-700">
              {fmtMoney(pulled)}
            </div>
          </div>
          <div className="border-l border-slate-200 pl-2" data-testid={`${testid}-paid`}>
            <div className="flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              Paid
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="rounded-full p-0 text-slate-400 hover:text-slate-700 focus:outline-none"
                    data-testid={`${testid}-paid-info`}
                    aria-label="What does Paid mean?"
                  >
                    <Info className="h-2.5 w-2.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-[11px] p-2 max-w-[280px]">
                  <p className="font-semibold mb-1">Paid — {fmtMoney(paid)}</p>
                  <p className="text-muted-foreground mb-1.5">
                    Sum of <code className="bg-muted text-foreground px-0.5 rounded font-mono">paid_amount</code> on
                    bills (BillPortal-paid only). We're still verifying and updating transactions, so a
                    few records may not yet reflect what actually moved through the bank.
                  </p>
                  {bankDebits != null && bankDebits > 0 && (
                    <p className="text-[10px] border-t pt-1 mt-1">
                      <span className="text-muted-foreground">From bank statement (debits):</span>{' '}
                      <span className="font-semibold tabular-nums">{fmtMoney(bankDebits)}</span>
                      <span className="text-muted-foreground"> (in current date range)</span>
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="text-xs font-semibold tabular-nums leading-tight text-slate-700">
              {fmtMoney(paid)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function DerivationRow({ label, formula, description, accent = 'text-slate-900' }) {
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className={`text-[12px] font-semibold ${accent}`}>{label}</span>
        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
          {formula}
        </code>
      </div>
      <p className="mt-1 text-slate-600">{description}</p>
    </div>
  );
}


/**
 * Properties tile: big top number = active ubm_locations count. Three sub-
 * stats below break down what's behind each property — Units (residential
 * spaces), Meters (sub-meters), and Accounts (utility + unit accounts
 * combined). Sourced from ubm_spaces, ubm_meters, ubm_utility_accounts and
 * ubm_unit_accounts on the kpis endpoint.
 */
function PropertiesTile({ properties, units, meters, accounts, testid }) {
  const fmt = (n) => Number(n || 0).toLocaleString();
  return (
    <Card className="border-slate-200/70 shadow-none" data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Properties</span>
          <Building className="h-4 w-4 text-slate-500" />
        </div>
        <div
          className="mt-1 text-xl font-bold tabular-nums text-slate-900"
          data-testid={`${testid}-amount`}
        >
          {fmt(properties)}
        </div>
        <div className="mt-1.5 grid grid-cols-3 gap-2 border-t border-slate-100 pt-1.5">
          <div data-testid={`${testid}-units`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Units</div>
            <div className="text-xs font-semibold tabular-nums leading-tight text-slate-700">
              {fmt(units)}
            </div>
          </div>
          <div className="border-l border-slate-200 pl-2" data-testid={`${testid}-meters`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Meters</div>
            <div className="text-xs font-semibold tabular-nums leading-tight text-slate-700">
              {fmt(meters)}
            </div>
          </div>
          <div className="border-l border-slate-200 pl-2" data-testid={`${testid}-accounts`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Accounts</div>
            <div className="text-xs font-semibold tabular-nums leading-tight text-slate-700">
              {fmt(accounts)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/**
 * "X vendors · Y accounts" stat strip rendered below each commodity chart.
 * Account count = (distinct utility/customer account numbers) ∪ (distinct
 * per-unit sub-meter account numbers). Server-side via $setUnion + $size.
 */
function CommodityFooterStats({ meta, commodity }) {
  const v = meta?.vendor_count ?? 0;
  const a = meta?.account_count ?? 0;
  return (
    <div
      className="mt-1 flex items-center justify-around gap-2 rounded-md border border-slate-200/70 bg-slate-50/50 px-3 py-2 text-[11px]"
      data-testid={`commodity-footer-stats-${commodity}`}
    >
      <div className="text-center" data-testid={`commodity-vendors-${commodity}`}>
        <div className="text-base font-semibold tabular-nums text-slate-900">{v.toLocaleString()}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Vendor{v === 1 ? '' : 's'}</div>
      </div>
      <div className="h-7 w-px bg-slate-200" aria-hidden />
      <div className="text-center" data-testid={`commodity-accounts-${commodity}`}>
        <div className="text-base font-semibold tabular-nums text-slate-900">{a.toLocaleString()}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Utility &amp; Unit Accts</div>
      </div>
    </div>
  );
}

function MonthlySpendBarChart({ monthly, large = false }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={(monthly.series || []).map((s) => ({
          ...s,
          month: format(new Date(s.month), 'yyyy-MM'),
        }))}
        margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 11, fill: '#64748b', fontFamily: 'inherit' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#64748b', fontFamily: 'inherit' }}
          tickFormatter={(v) => fmtMoney(v)}
          tickLine={false}
          axisLine={false}
        />
        <RTooltip
          formatter={(v) => fmtMoneyFull(v)}
          cursor={{ fill: 'rgba(148,163,184,0.1)' }}
          contentStyle={{
            fontFamily: 'inherit',
            fontSize: 12,
            borderRadius: 6,
            border: '1px solid rgb(226,232,240)',
            boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
            padding: '6px 10px',
          }}
          labelStyle={{ fontSize: 11, color: '#475569', fontWeight: 600, marginBottom: 2 }}
          itemStyle={{ fontSize: 12, padding: '1px 0' }}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, fontFamily: 'inherit', paddingTop: 4 }}
          iconType="circle"
          iconSize={8}
        />
        <Bar dataKey="electricity" fill={COLORS.electricity} radius={[4, 4, 0, 0]} name={`Electricity ${fmtMoney(monthly.totals?.electricity)}`} />
        <Bar dataKey="water" fill={COLORS.water} radius={[4, 4, 0, 0]} name={`Water ${fmtMoney(monthly.totals?.water)}`} />
        <Bar dataKey="gas" fill={COLORS.gas} radius={[4, 4, 0, 0]} name={`Gas ${fmtMoney(monthly.totals?.gas)}`} />
        <Bar dataKey="trash" fill={COLORS.trash} radius={[4, 4, 0, 0]} name={`Trash ${fmtMoney(monthly.totals?.trash)}`} />
        <Bar dataKey="sewer" fill={COLORS.sewer} radius={[4, 4, 0, 0]} name={`Sewer ${fmtMoney(monthly.totals?.sewer)}`} />
      </BarChart>
    </ResponsiveContainer>
  );
}


function CommodityHero({ gradient, icon, title, val, unit, testid }) {
  return (
    <Card className="overflow-hidden border-0 rounded-b-none" data-testid={testid}>
      <div className={`bg-gradient-to-br ${gradient} relative px-6 py-7 text-white`}>
        <div className="text-lg font-medium opacity-95">{title}</div>
        <div className="mt-3 flex items-baseline gap-3">
          <span className="opacity-80">{icon}</span>
          <span className="text-4xl font-bold tracking-tight">{val}</span>
          <span className="text-base font-medium opacity-90">{unit}</span>
        </div>
      </div>
    </Card>
  );
}

function SitesMap({ items }) {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef(null);

  const sitesWithCoords = useMemo(
    () => (items || []).filter(
      (s) => Number.isFinite(s.lat) && Number.isFinite(s.lng) && s.lat !== 0 && s.lng !== 0,
    ),
    [items],
  );

  // Mount Leaflet directly via a ref. This bypasses react-leaflet's StrictMode
  // double-mount fragility entirely — Leaflet creates the map once on the
  // <div> ref, and we tear it down on unmount.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    // Defensive cleanup if a previous mount left a marker on the DOM node.
    if (node._leaflet_id) {
      delete node._leaflet_id;
    }
    const map = L.map(node, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    }).setView([37.8, -96], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    markersRef.current = L.layerGroup().addTo(map);
    return () => {
      try {
        map.remove();
      } catch {
        /* ignore */
      }
      mapRef.current = null;
      markersRef.current = null;
    };
  }, []);

  // Re-render markers whenever sites change.
  useEffect(() => {
    const map = mapRef.current;
    const layer = markersRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (sitesWithCoords.length === 0) {
      map.setView([37.8, -96], 4);
      return;
    }
    sitesWithCoords.forEach((s) => {
      const m = L.circleMarker([s.lat, s.lng], {
        radius: 6,
        color: '#ef4444',
        fillColor: '#ef4444',
        fillOpacity: 0.85,
        weight: 1,
      });
      m.bindTooltip(
        `<div style="font-size:11px"><div style="font-weight:600">${s.sitename || ''}</div>` +
        `<div>${s.city || ''}, ${s.state || ''}</div>` +
        `<div>${fmtMoney(s.total_spend)}</div></div>`,
      );
      m.addTo(layer);
    });
    const bounds = L.latLngBounds(sitesWithCoords.map((s) => [s.lat, s.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.2), { animate: false });
    }
  }, [sitesWithCoords]);

  return (
    <div className="org-dash-leaflet h-[400px] overflow-hidden rounded-md">
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} data-testid="sites-map-canvas" />
    </div>
  );
}

// Keep a leaflet crash from taking down the whole dashboard.
class MapErrorBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('SitesMap crashed:', err); }
  render() {
    if (this.state.failed) {
      return (
        <div className="flex h-[360px] items-center justify-center rounded-md bg-slate-100 text-xs text-muted-foreground">
          Map unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

function downloadCsv(items) {
  const header = ['Property', 'State', 'City', 'Utilities', 'Total Spend'];
  const rows = (items || []).map((r) => [
    r.sitename || '', r.state || '', r.city || '', r.utilities, r.total_spend,
  ]);
  const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `spend-comparison-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}
